import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet } from "lucide-react";

const FORMATS = [
  { key: "tally", label: "Tally (XML)", ext: "xml" },
  { key: "zoho", label: "Zoho Books", ext: "csv" },
  { key: "quickbooks", label: "QuickBooks", ext: "csv" },
  { key: "vyapar", label: "Vyapar", ext: "csv" },
];

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

async function downloadExport(format, from, to) {
  const { data, headers } = await api.get("/accounting/export", {
    params: { format, from, to },
    responseType: "blob",
  });
  const disp = headers["content-disposition"] || "";
  const match = /filename="?([^"]+)"?/i.exec(disp);
  const filename = match?.[1] || `ojent-${format}-${from}-${to}.${format === "tally" ? "xml" : "csv"}`;
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Accounting() {
  const [range, setRange] = useState(defaultRange);
  const [downloading, setDownloading] = useState(null);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["accounting-summary", range.from, range.to],
    queryFn: async () => (await api.get(`/accounting/summary?from=${range.from}&to=${range.to}`)).data,
  });

  const exportFormat = async (format) => {
    setDownloading(format);
    try {
      await downloadExport(format, range.from, range.to);
      toast.success(`Downloaded ${format} export`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="accounting-page">
      <PageHeader title="Accounting Export" subtitle="GST summary and exports for Tally, Zoho, QuickBooks, Vyapar" />

      <div className="flex flex-wrap items-end gap-3 mb-5 max-w-3xl">
        <div>
          <Label>From</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5 max-w-4xl">
        {isLoading || !summary ? (
          <p className="text-neutral-400 col-span-full">Loading summary…</p>
        ) : (
          <>
            <Stat label="Paid invoices" value={summary.count} />
            <Stat label="Taxable" value={money(summary.taxable)} />
            <Stat label="CGST + SGST" value={money((summary.cgst || 0) + (summary.sgst || 0))} />
            <Stat label="Total" value={money(summary.total)} accent />
          </>
        )}
      </div>

      <div className="kul-card p-5 max-w-3xl">
        <h3 className="font-display font-700 flex items-center gap-2 mb-4">
          <FileSpreadsheet className="w-4 h-4" /> Download exports
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              variant="outline"
              disabled={!!downloading}
              onClick={() => exportFormat(f.key)}
              className="justify-start h-11"
              data-testid={`export-${f.key}`}
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading === f.key ? "Downloading…" : f.label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-neutral-400 mt-4">
          Exports use paid orders in the selected date range. Files open via authenticated blob download.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="kul-card p-4">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`font-display font-700 text-xl mt-1 ${accent ? "text-[var(--kul-primary)]" : ""}`}>{value}</div>
    </div>
  );
}
